import get from 'lodash/get';
import refinementsFromUrl from './searchResult/model/refinementsFromUrl';
import queryFromRefinements from './searchResult/model/queryFromRefinements';
import listingFetchService from './searchResult/model/listingFetchService';
import fetchInvestListings from './searchResult/model/fetchInvestListings';
import { getListingDetails } from './detail/model/detailBffRepository';
import { getElitePlusCarouselListings } from './homepage/model/homepageBffRepository';
import * as listingIdExtractor from './listingIdExtractor';
import { NotFoundError } from './router';
import { getStore } from './app/globalStore';
import { hideProgressLoader, showProgressLoader } from './app/actions/creators';
import { parseUrlChannel, channelFromUrl } from '../shared/model/constants/channels';
import mapFetchService from './searchResult/model/mapFetchService';
import { logError } from '../shared/log';
import { fetchOptimizelyDatafile } from '../shared/requests/optimizelyRequest';

const getPageData = () => {
  if (typeof window === 'undefined') {
    return Promise.resolve(undefined);
  }

  const data = get(window, 'REA.pageData');

  if (window && window.REA) {
    window.REA.pageData = undefined;
  }

  return Promise.resolve(data);
};

const fetchDataWithSpinner = (fetchDataFunc, ...args) => {
  const { dispatch } = getStore();
  dispatch(showProgressLoader());

  return fetchDataFunc(...args)
    .then((response) => {
      dispatch(hideProgressLoader());
      return response;
    })
    .catch((error) => {
      dispatch(hideProgressLoader());
      return Promise.reject(error);
    });
};

const buildSRPAndInvestQuery = (url) =>
  new Promise((resolve, reject) => {
    const refinements = refinementsFromUrl(url);
    if (!refinements) {
      return reject(new NotFoundError('Failed to parse refinement url'));
    }
    const query = queryFromRefinements(refinements);

    return resolve(query);
  });

function NotFoundListingError({ siteSection, status }) {
  this.siteSection = siteSection;
  this.status = status;
  this.message = 'could not find listingId';
}

export const srpFetchData = ({ url, featureFlags }) =>
  getPageData().then((data) => {
    if (data) {
      return data;
    }

    return buildSRPAndInvestQuery(url)
      .then((query) => fetchDataWithSpinner(listingFetchService, query, featureFlags))
      .catch((error) => {
        logError(error);
      });
  });

export const mapFetchData = ({ url, featureFlags }) =>
  getPageData().then((data) => {
    if (data) {
      return data;
    }

    return buildSRPAndInvestQuery(url)
      .then((query) => fetchDataWithSpinner(mapFetchService, query, featureFlags))
      .catch((error) => {
        logError(error);
      });
  });

export const investFetchData = ({ url, featureFlags }) =>
  getPageData().then((data) => {
    if (data) {
      return data;
    }

    return buildSRPAndInvestQuery(url).then((query) =>
      fetchDataWithSpinner(fetchInvestListings, query, featureFlags),
    );
  });

export const detailFetchData = ({ url, params: { channel }, featureFlags }) =>
  getPageData().then((data) => {
    if (data) {
      if (!data.error) {
        return data;
      }
      if ([410, 404].indexOf(data.error.status) > -1) {
        const urlChannel = url.split('/').filter((item) => item.length > 0)[0];
        const channelObj = channelFromUrl(urlChannel);
        const { status } = data.error;
        throw new NotFoundListingError({
          siteSection: channelObj ? channelObj.tealium : undefined,
          status,
        });
      }
    }

    const listingId = listingIdExtractor.extractListingId(url);
    const channelObject = channel ? parseUrlChannel(channel) : undefined;

    return fetchDataWithSpinner(getListingDetails, listingId, channelObject, featureFlags).then(
      (pageData) =>
        fetchOptimizelyDatafile()
          .then((optimizelyDataFile) => {
            if (!optimizelyDataFile) {
              return pageData;
            }

            return {
              ...pageData,
              optimizelyDataFile,
            };
          })
          .catch(() => pageData),
    );
  });

// TODO: [RCOTA POC] Refactor or remove this file depending on how the PoC progresses,
// this also needs to be tested
export const fetchLatestListings = () =>
  getPageData().then((data) => {
    if (data) {
      if (!data.error) {
        return data;
      }
    }
    return getElitePlusCarouselListings('rcota-latest-listings');
  });
